import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './RenewalBanner.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiClient() {
  const token = localStorage.getItem('token');
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

// ── Banner message and button label per status ────────────────────────────────
//
// Uses member.status directly (set by the server / cron) — never derives
// status client-side. lapse_date is calculated from expiry_date +
// tenant_settings.renewal_grace_period_days.

function getBannerProps(member) {
  const status    = member?.status;
  const graceDays = member?.tenant_settings?.renewal_grace_period_days ?? 30;
  const lapseDate = fmtDate(addDays(member?.expiry_date, graceDays));

  switch (status) {
    case 'due':
      return {
        modifier:    'due',
        message:     `Your membership expires on ${fmtDate(member.expiry_date)} — renew now to avoid interruption.`,
        buttonLabel: 'Renew Now',
      };
    case 'overdue':
      return {
        modifier:    'overdue',
        message:     `Your membership expired on ${fmtDate(member.expiry_date)}. You have until ${lapseDate} to renew.`,
        buttonLabel: 'Renew Now',
      };
    case 'lapsed':
      return {
        modifier:    'lapsed',
        message:     'Your membership has lapsed. Rejoin to restore your member benefits.',
        buttonLabel: 'Rejoin Now',
      };
    default:
      return null;
  }
}

// ── RenewalForm ───────────────────────────────────────────────────────────────

function RenewalForm({ amount, clientSecret, onSuccess, onCancel }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError]           = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: { card: elements.getElement(CardElement) } }
      );
      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }
      const res = await apiClient().post('/api/members/renew/confirm', {
        paymentIntentId: paymentIntent.id,
      });
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form className="a-renewal-form" onSubmit={handleSubmit}>
      <p className="a-renewal-form__fee">
        Renewal fee: <strong>${parseFloat(amount).toFixed(2)} AUD</strong>
      </p>
      <div className="a-renewal-form__card">
        <CardElement options={{ style: { base: { fontSize: '16px', color: '#374151' } } }} />
      </div>
      {error && <p className="a-renewal-form__error">{error}</p>}
      <div className="a-renewal-form__actions">
        <button
          type="submit"
          className="a-renewal-form__submit"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing…' : `Pay $${parseFloat(amount).toFixed(2)}`}
        </button>
        <button
          type="button"
          className="a-renewal-form__cancel"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── RenewalModal ──────────────────────────────────────────────────────────────

function RenewalModal({ member, onSuccess, onClose }) {
  const [step, setStep]               = useState('loading');
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount]           = useState(0);
  const [errMsg, setErrMsg]           = useState('');

  const [stripePromise] = useState(
    () => member.stripe_publishable_key ? loadStripe(member.stripe_publishable_key) : null
  );

  useEffect(() => {
    apiClient()
      .post('/api/members/renew/create-payment-intent')
      .then(res => {
        if (res.data.free) {
          setStep('free');
        } else {
          setClientSecret(res.data.clientSecret);
          setAmount(res.data.amount);
          setStep('ready');
        }
      })
      .catch(err => {
        setErrMsg(err.response?.data?.error || 'Failed to start renewal.');
        setStep('error');
      });
  }, []);

  const handleFreeRenew = async () => {
    setStep('loading');
    try {
      const res = await apiClient().post('/api/members/renew/confirm', {});
      onSuccess(res.data);
    } catch (err) {
      setErrMsg(err.response?.data?.error || 'Renewal failed.');
      setStep('error');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isRejoin = member.status === 'lapsed';

  return (
    <div className="a-renewal-modal-backdrop" onClick={handleBackdropClick}>
      <div className="a-renewal-modal">
        <h2 className="a-renewal-modal__title">
          {isRejoin ? 'Rejoin' : 'Renew Membership'}
        </h2>
        <p className="a-renewal-modal__subtitle">
          Membership: <strong>{member.membership_number}</strong>
        </p>

        {step === 'loading' && (
          <p className="a-renewal-modal__loading">Loading payment details…</p>
        )}

        {step === 'error' && (
          <>
            <p className="a-renewal-modal__error">{errMsg}</p>
            <button className="a-renewal-modal__close-btn" onClick={onClose}>
              Close
            </button>
          </>
        )}

        {step === 'free' && (
          <>
            <p className="a-renewal-free__msg">Your membership can be renewed at no cost.</p>
            <button className="a-renewal-free__btn" onClick={handleFreeRenew}>
              {isRejoin ? 'Rejoin Now' : 'Renew Now'}
            </button>
          </>
        )}

        {step === 'ready' && clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <RenewalForm
              amount={amount}
              clientSecret={clientSecret}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        )}

        {step === 'ready' && (!clientSecret || !stripePromise) && (
          <p className="a-renewal-modal__error">
            Online payments are not configured. Please contact your administrator.
          </p>
        )}
      </div>
    </div>
  );
}

// ── RenewalBanner ─────────────────────────────────────────────────────────────

function RenewalBanner({ member, onRenewSuccess }) {
  const [showModal, setShowModal] = useState(false);

  const bannerProps = getBannerProps(member);
  if (!bannerProps) return null;

  const handleRenewClick = () => setShowModal(true);

  const handleModalClose = () => setShowModal(false);

  const handlePaymentSuccess = (data) => {
    setShowModal(false);
    onRenewSuccess(data);
  };

  return (
    <>
      <div className={`a-renewal-banner a-renewal-banner--${bannerProps.modifier}`}>
        <p className="a-renewal-banner__message">{bannerProps.message}</p>
        <button className="a-renewal-banner__btn" onClick={handleRenewClick}>
          {bannerProps.buttonLabel}
        </button>
      </div>

      {showModal && (
        <RenewalModal
          member={member}
          onSuccess={handlePaymentSuccess}
          onClose={handleModalClose}
        />
      )}
    </>
  );
}

export default RenewalBanner;
